// API base URL - production builds call worker directly, dev uses Vite proxy
// Build version: 2025-12-03-v4 - Simplified API key auth
const WORKER_API = 'https://atlas-ops-agent.atlas-kitchen.workers.dev/api';
const API_BASE = import.meta.env.DEV ? '/api' : WORKER_API;

function getAuthToken(): string | null {
  return localStorage.getItem('admin_token');
}

export function setAuthToken(token: string) {
  localStorage.setItem('admin_token', token);
}

export function clearAuthToken() {
  localStorage.removeItem('admin_token');
}

export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { 'X-Admin-Token': token } : {}),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearAuthToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    // Backend returns { error: "..." } but some endpoints return { message: "..." }
    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Auth
export async function login(password: string): Promise<{ success: boolean }> {
  return fetchApi('/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password, apiMode: true }),
  });
}

// Dashboard
export interface DashboardStats {
  totalConversations: number;
  totalMessages: number;
  totalVectors: number;
  avgConfidence: number;
  syncStatus: {
    help: { lastSync: string; count: number };
    slack: { lastSync: string; count: number };
    linear: { lastSync: string; count: number };
  };
}

export async function getDashboardStats(): Promise<DashboardStats> {
  return fetchApi('/admin/stats');
}

// Conversations
export interface ConfidenceBreakdown {
  overall: number;
  sourceQuality: number;
  relevance: number;
  resultCount: number;
  evidenceQuality: number;
  sources?: string[];
}

export interface Conversation {
  id: string;
  userId: string;
  channel: string;
  threadTs: string;
  messageTs?: string; // Slack message timestamp - used for thread linking
  query: string;
  response: string;
  messages?: Message[]; // Thread messages
  confidence: number | null;
  confidenceBreakdown?: ConfidenceBreakdown | null; // Detailed confidence score breakdown
  sources: string[];
  createdAt: string;
  evalScore?: number;
  evalNotes?: string;
  feedback?: 'positive' | 'negative' | null;
  model?: string;
  responseTimeSeconds?: number | null;
  displayName?: string | null; // User display name from Slack
  isThread?: boolean;
  parentConversationId?: string | null;
  threadRootId?: string | null;
  ticketId?: string | null; // Link to ticket for AI draft conversations
  metadata?: {
    channel?: string;
    ticketId?: string;
    ragExecutionId?: string;
    agentEmail?: string;
  } | null;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  messageTs?: string;
  metadata?: Record<string, unknown>;
  conversationId?: string;
}

export async function getConversations(params?: {
  limit?: number;
  offset?: number;
  search?: string;
  feedback?: 'positive' | 'negative' | 'none' | '';
  channel?: 'slack' | 'ticket_ai_draft' | '';
}): Promise<{ conversations: Conversation[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.search) query.set('search', params.search);
  if (params?.feedback) query.set('feedback', params.feedback);
  if (params?.channel) query.set('channel', params.channel);
  return fetchApi(`/admin/conversations?${query}`);
}

export async function getConversation(id: string): Promise<Conversation> {
  return fetchApi(`/admin/conversations/${id}`);
}

export async function updateConversationEval(id: string, data: {
  evalScore?: number;
  evalNotes?: string;
}): Promise<Conversation> {
  return fetchApi(`/admin/conversations/${id}/eval`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Agent Trace Types
export interface TracePhaseData {
  name: string;
  durationMs?: number;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
  data: Record<string, unknown>;
}

export interface TracePhase1Data {
  originalQuery: string;
  enrichedQuery?: string;
  searchTerms?: string[];
  detectedAbbreviations?: Array<{ abbrev: string; full: string }>;
  isThreadReply: boolean;
  threadContext?: Array<{ role: string; content: string }>;
}

export interface TracePhase2Data {
  totalResults?: number;
  topScore?: number;
  resultsPreview?: Array<{ id: string; title: string; score: number }>;
  allResults?: Array<{
    id: string;
    title: string;
    url: string;
    source: string;
    score: number;
    combinedScore: number;
    contentPreview: string;
  }>;
}

export interface TracePhase3Data {
  hasResults: boolean;
  isRelevantTopic: boolean;
  responseType?: 'answer' | 'no_results_relevant' | 'no_results_off_topic';
}

export interface TracePhase4Data {
  reasonerThinking?: string;
  reasonerAnswer?: string;
  formattedResponse?: string;
  fallbackUsed: boolean;
}

export interface ConversationTrace {
  hasTrace: boolean;
  conversationId: string;
  message?: string; // When hasTrace is false
  executionId?: string;
  status?: 'completed' | 'error' | 'timeout';
  createdAt?: string;

  summary?: {
    totalDurationMs?: number;
    totalInputTokens?: number;
    totalOutputTokens?: number;
    estimatedCostUsd?: number;
    workflowModelPreset?: string;
  };

  phase1?: {
    name: string;
    durationMs?: number;
    model?: string;
    inputTokens?: number;
    outputTokens?: number;
    data: TracePhase1Data;
  };

  phase2?: {
    name: string;
    durationMs?: number;
    embeddingDurationMs?: number;
    searchDurationMs?: number;
    embeddingModel?: string;
    embeddingTokens?: number;
    data: TracePhase2Data;
  };

  phase3?: {
    name: string;
    durationMs?: number;
    model?: string;
    inputTokens?: number;
    outputTokens?: number;
    data: TracePhase3Data;
  };

  phase4?: {
    name: string;
    durationMs?: number;
    reasonerModel?: string;
    formatterModel?: string;
    reasonerInputTokens?: number;
    reasonerOutputTokens?: number;
    formatterInputTokens?: number;
    formatterOutputTokens?: number;
    data: TracePhase4Data;
  };

  error?: {
    phase?: string;
    message?: string;
    stack?: string;
  } | null;

  slackContext?: {
    eventType?: string;
    channelId?: string;
    threadTs?: string;
    userId?: string;
    eventTs?: string;
    responseSentAt?: string;
    responseUpdateCount?: number;
  };
}

export async function getConversationTrace(id: string): Promise<ConversationTrace> {
  return fetchApi(`/admin/conversations/${id}/trace`);
}

// Vector Search
export interface VectorSearchResult {
  id: string;
  content: string;
  source: string;
  url?: string;
  score: number;
  metadata: Record<string, unknown>;
}

export interface VectorStats {
  totalVectors: number;
  bySource: Record<string, number>;
  indexName: string;
  dimensions: number;
}

export async function getVectorStats(): Promise<VectorStats> {
  return fetchApi('/admin/vectors/stats');
}

export async function searchVectors(query: string, limit = 10): Promise<VectorSearchResult[]> {
  return fetchApi('/admin/vectors/search', {
    method: 'POST',
    body: JSON.stringify({ query, limit }),
  });
}

export async function purgeVectors(source: 'slack' | 'notion' | 'help' | 'linear' | 'all'): Promise<{ success: boolean; message: string; deleted: number }> {
  return fetchApi('/admin/vectors/purge', {
    method: 'POST',
    body: JSON.stringify({ source }),
  });
}

// Vector Index (granular management)
export interface VectorIndexEntry {
  id: string;
  source: 'slack' | 'notion' | 'help' | 'linear';
  sourceId: string;
  title: string | null;
  contentPreview: string | null;
  url: string | null;
  createdAt: string;
  metadata: Record<string, unknown> | null;
  existsInVectorize?: boolean;
}

export interface VectorListResponse {
  vectors: VectorIndexEntry[];
  total: number;
  limit: number;
  offset: number;
}

export async function listVectors(params?: {
  source?: 'slack' | 'notion' | 'help' | 'linear';
  limit?: number;
  offset?: number;
}): Promise<VectorListResponse> {
  const query = new URLSearchParams();
  if (params?.source) query.set('source', params.source);
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return fetchApi(`/admin/vectors/list?${query}`);
}

export async function getVector(id: string): Promise<VectorIndexEntry & { existsInVectorize: boolean }> {
  return fetchApi(`/admin/vectors/${encodeURIComponent(id)}`);
}

export async function deleteVectors(ids: string[]): Promise<{ success: boolean; deleted: number; message: string }> {
  return fetchApi('/admin/vectors/delete', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

// Sync Management
export interface SyncJob {
  id: string;
  source: 'help' | 'slack' | 'linear' | 'notion';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt?: string;
  completedAt?: string;
  itemsProcessed: number;
  error?: string;
}

export async function getSyncJobs(): Promise<SyncJob[]> {
  return fetchApi('/admin/sync/jobs');
}

export async function triggerSync(source: string, mode: 'recent' | 'full' = 'recent'): Promise<SyncJob> {
  const query = mode === 'full' ? '?mode=full' : '';
  return fetchApi(`/admin/sync/${source}${query}`, { method: 'POST' });
}

export interface JobLog {
  timestamp: string;
  level: string;
  message: string;
}

export interface JobDetails {
  job: SyncJob;
  logs: JobLog[];
}

export async function getJobLogs(jobId: string): Promise<JobDetails> {
  return fetchApi(`/admin/sync/jobs/${jobId}/logs`);
}

export async function cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
  return fetchApi(`/admin/sync/jobs/${jobId}/cancel`, { method: 'POST' });
}

export async function retryJob(jobId: string): Promise<{ success: boolean; message: string; newJobId?: string }> {
  return fetchApi(`/admin/sync/jobs/${jobId}/retry`, { method: 'POST' });
}

export async function processQueue(): Promise<{ success: boolean; message: string }> {
  return fetchApi('/admin/sync/process-queue', { method: 'POST' });
}

// Model Configuration
export interface ModelConfig {
  id: string;
  name: string;
  provider: string;
  description: string;
  contextWindow: number;
  costPer1kInput: number;
  costPer1kOutput: number;
  toolSupport: boolean;
  speed: 'fast' | 'medium' | 'slow' | 'free';
}

export interface CurrentModelConfig {
  selectedModelId: string;
  model: ModelConfig;
}

export interface AvailableModelsResponse {
  models: ModelConfig[];
  defaultModelId: string;
}

export async function getModelConfig(): Promise<CurrentModelConfig> {
  return fetchApi('/admin/config/model');
}

export async function setModelConfig(modelId: string): Promise<{
  success: boolean;
  message: string;
  selectedModelId: string;
  model: ModelConfig;
}> {
  return fetchApi('/admin/config/model', {
    method: 'PUT',
    body: JSON.stringify({ modelId }),
  });
}

export async function getAvailableModels(): Promise<AvailableModelsResponse> {
  return fetchApi('/admin/config/models');
}

// Knowledge Sources
export interface KnowledgeSource {
  id: string;
  source_type: 'help' | 'slack' | 'linear' | 'notion';
  source_id: string;
  name: string;
  url: string | null;
  config: Record<string, unknown> | null;
  enabled: boolean;
  last_synced_at: string | null;
  sync_status: 'pending' | 'syncing' | 'synced' | 'error';
  sync_error: string | null;
  created_at: string;
  updated_at: string;
}

export async function getSources(): Promise<{ sources: KnowledgeSource[]; total: number }> {
  return fetchApi('/admin/sources');
}

export async function getSource(id: string): Promise<{ source: KnowledgeSource }> {
  return fetchApi(`/admin/sources/${encodeURIComponent(id)}`);
}

export async function createSource(data: {
  source_type: 'help' | 'slack' | 'linear' | 'notion';
  source_id: string;
  name: string;
  url?: string;
  config?: Record<string, unknown>;
}): Promise<{ success: boolean; id: string; message: string }> {
  return fetchApi('/admin/sources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSource(id: string, data: {
  name?: string;
  url?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
}): Promise<{ success: boolean; message: string }> {
  return fetchApi(`/admin/sources/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSource(id: string): Promise<{ success: boolean; message: string }> {
  return fetchApi(`/admin/sources/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function syncSource(id: string): Promise<{ success: boolean; jobId: string; message: string }> {
  return fetchApi(`/admin/sources/${encodeURIComponent(id)}/sync`, {
    method: 'POST',
  });
}

export async function seedSources(): Promise<{ success: boolean; inserted: number; skipped: number; total: number; message: string }> {
  return fetchApi('/admin/sources/seed', {
    method: 'POST',
  });
}

// Golden Answers (self-learning feedback loop)
export interface GoldenAnswer {
  id: string;
  conversation_id: string;
  query: string;
  approved_response: string;
  raw_response?: string;
  formatted_response?: string;
  citations?: Array<{ url: string; label: string; position: number }>;
  is_admin_edited?: boolean;
  formatting_model?: string;
  eval_score: number;
  created_by: string | null;
  vector_id: string | null;
  created_at: string;
  original_query?: string;
  original_response?: string;
}

export interface GoldenAnswersResponse {
  goldenAnswers: GoldenAnswer[];
  total: number;
  limit: number;
  offset: number;
}

// Re-export types from slack-styles for consistency
export type { Citation, SlackBlock } from './slack-styles';
import type { Citation, SlackBlock } from './slack-styles';

// Format preview response
export interface FormatPreviewResponse {
  formattedText: string;
  blocks: SlackBlock[];
  extractedCitations: Citation[];
  model: string;
}

export async function getGoldenAnswers(params?: {
  limit?: number;
  offset?: number;
}): Promise<GoldenAnswersResponse> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  return fetchApi(`/admin/golden-answers?${query}`);
}

/**
 * Format a raw answer with citations using AI
 * Used for live preview while admin is editing
 */
export async function formatGoldenAnswerPreview(data: {
  rawAnswer: string;
  query: string;
}): Promise<FormatPreviewResponse> {
  return fetchApi('/admin/golden-answers/format-preview', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createGoldenAnswer(data: {
  conversationId: string;
  query: string;
  approvedResponse: string;
  rawResponse?: string;
  formattedResponse?: string;
  citations?: Citation[];
  isAdminEdited?: boolean;
  evalScore: number;
}): Promise<{
  success: boolean;
  goldenAnswer: {
    id: string;
    vectorId: string;
    conversationId: string;
    query: string;
    approvedResponse: string;
    rawResponse?: string;
    formattedResponse?: string;
    citations?: Citation[];
    isAdminEdited: boolean;
    evalScore: number;
  };
}> {
  return fetchApi('/admin/golden-answers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteGoldenAnswer(id: string): Promise<{ success: boolean; id: string }> {
  return fetchApi(`/admin/golden-answers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ========== Workflow Configuration API ==========

export interface WorkflowStageConfig {
  modelId: string;
  prompt?: string;
  temperature: number;
  maxTokens: number;
}

export interface RAGWorkflowConfig {
  queryExpander: WorkflowStageConfig;
  assessor: WorkflowStageConfig;
  reasoner: WorkflowStageConfig;
  formatter: WorkflowStageConfig;
}

export interface SourceWeightsConfig {
  golden: number;
  help: number;
  linear: number;
  notion: number;
  slack: number;
}

export interface AllActiveConfigs {
  ragWorkflow: RAGWorkflowConfig | null;
  sourceWeights: SourceWeightsConfig | null;
  prompts: null; // Not used yet
}

export interface WorkflowConfigHistoryItem {
  id: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  createdBy: {
    id: string;
    username: string | null;
    displayName: string | null;
  };
  notes: string | null;
  type?: string;
  data?: RAGWorkflowConfig | SourceWeightsConfig | Record<string, unknown>;
}

export interface WorkflowModelInfo {
  id: string;
  name: string;
  provider: string;
  description: string;
  speed: 'fast' | 'medium' | 'slow' | 'free';
  toolSupport: boolean;
  costPer1kInput: number;
  costPer1kOutput: number;
}

// Get all active workflow configs
export async function getWorkflowConfigs(): Promise<{
  success: boolean;
  configs: AllActiveConfigs;
}> {
  return fetchApi('/admin/workflows/config');
}

// Get specific config type
export async function getWorkflowConfig<T extends RAGWorkflowConfig | SourceWeightsConfig>(
  type: 'rag_workflow' | 'source_weights'
): Promise<{
  success: boolean;
  config: T;
  version: number;
  createdAt: string;
}> {
  return fetchApi(`/admin/workflows/config?type=${type}`);
}

// Get config version history
export async function getWorkflowConfigHistory(
  type: 'rag_workflow' | 'source_weights',
  limit: number = 20,
  offset: number = 0
): Promise<{
  success: boolean;
  history: WorkflowConfigHistoryItem[];
}> {
  return fetchApi(`/admin/workflows/config/history?type=${type}&limit=${limit}&offset=${offset}`);
}

// Get specific config by ID
export async function getWorkflowConfigById(configId: string): Promise<{
  success: boolean;
  config: {
    id: string;
    version: number;
    type: string;
    data: RAGWorkflowConfig | SourceWeightsConfig;
    isActive: boolean;
    createdAt: string;
    createdBy: {
      id: string;
      username: string | null;
      displayName: string | null;
    };
    notes: string | null;
  };
}> {
  return fetchApi(`/admin/workflows/config/${encodeURIComponent(configId)}`);
}

// Save new config version
export async function saveWorkflowConfig(data: {
  type: 'rag_workflow' | 'source_weights';
  data: RAGWorkflowConfig | SourceWeightsConfig;
  notes?: string;
}): Promise<{
  success: boolean;
  message: string;
  config: {
    id: string;
    version: number;
    type: string;
    isActive: boolean;
    createdAt: string;
  };
}> {
  return fetchApi('/admin/workflows/config', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Activate a specific config version (rollback)
export async function activateWorkflowConfig(configId: string): Promise<{
  success: boolean;
  message: string;
}> {
  return fetchApi(`/admin/workflows/config/${encodeURIComponent(configId)}/activate`, {
    method: 'PUT',
  });
}

// Get available models for workflow stages
export async function getWorkflowModels(): Promise<{
  success: boolean;
  models: WorkflowModelInfo[];
}> {
  return fetchApi('/admin/workflows/models');
}

// ============================================================================
// Scoring API
// ============================================================================

export type ScorerType = 'query_expander' | 'retrieval' | 'reasoner' | 'formatter' | 'end_to_end';
export type JudgeType = 'deterministic' | 'llm' | 'human';

export interface ScorerResult {
  score: number;
  details: Record<string, number>;
  reasoning?: string;
  judgeType: JudgeType;
  judgeModel?: string;
}

export interface ExecutionScores {
  executionId: string;
  queryExpander?: ScorerResult;
  retrieval?: ScorerResult;
  reasoner?: ScorerResult;
  formatter?: ScorerResult;
  endToEnd?: ScorerResult;
  overallScore: number;
  timestamp: string;
}

export interface ScoreRecord {
  id: string;
  executionId: string;
  scorerType: ScorerType;
  judgeType: JudgeType;
  score: number;
  details: string;
  reasoning?: string;
  judgeModel?: string;
  humanRaterId?: string;
  humanNotes?: string;
  createdAt: string;
}

export interface EvalRun {
  id: string;
  name?: string;
  startedAt: string;
  completedAt?: string;
  testCount: number;
  passedCount: number;
  failedCount: number;
  avgScore?: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  // TPR/TNR metrics
  true_positive_rate?: number | null;
  true_negative_rate?: number | null;
  true_positives?: number;
  false_positives?: number;
  true_negatives?: number;
  false_negatives?: number;
}

export interface EvalTestCase {
  id: string;
  name: string;
  description?: string;
  query: string;
  category?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  minScoreThreshold: number;
  isActive: boolean;
  createdAt: string;
  // Eval best practices fields
  dataset_split?: 'train' | 'dev' | 'test';
  ground_truth_pass?: boolean | null;
  ground_truth_reasoning?: string | null;
  labeled_by?: string | null;
  labeled_at?: string | null;
}

// Get scores for a specific execution
export async function getExecutionScores(executionId: string): Promise<{
  executionId: string;
  scores: ExecutionScores | null;
}> {
  return fetchApi(`/scores/${encodeURIComponent(executionId)}`);
}

// Get detailed score records for an execution
export async function getScoreDetails(executionId: string): Promise<{
  executionId: string;
  records: ScoreRecord[];
  count: number;
}> {
  return fetchApi(`/scores/${encodeURIComponent(executionId)}/details`);
}

// Submit a human rating
export async function submitHumanRating(
  executionId: string,
  scorerType: ScorerType,
  score: number,
  notes?: string
): Promise<{
  success: boolean;
  scoreId: string;
  message: string;
}> {
  return fetchApi('/scores/human-rating', {
    method: 'POST',
    body: JSON.stringify({ executionId, scorerType, score, notes }),
  });
}

// Get score trends over time
export async function getScoreTrends(days: number = 30): Promise<{
  trends: Array<{ date: string; avgScore: number; count: number }>;
  days: number;
}> {
  return fetchApi(`/scores/trends?days=${days}`);
}

// Get aggregated scores by stage and judge type
export async function getScoreAggregates(days: number = 30): Promise<{
  period: { startDate: string; endDate: string };
  byStage: Record<ScorerType, number>;
  byJudge: Record<JudgeType, number>;
}> {
  return fetchApi(`/scores/aggregates?days=${days}`);
}

// List eval runs
export async function listEvalRuns(limit: number = 20, offset: number = 0): Promise<{
  runs: EvalRun[];
  total: number;
  limit: number;
  offset: number;
}> {
  return fetchApi(`/evals?limit=${limit}&offset=${offset}`);
}

// Get eval run details
export async function getEvalRun(runId: string): Promise<{
  run: EvalRun & {
    scoresByStage?: Record<ScorerType, number>;
    scoresByJudge?: Record<JudgeType, number>;
    // TPR/TNR from detailed view
    tprTnr?: {
      tpr: number | null;
      tnr: number | null;
      confusionMatrix: {
        tp: number;
        fp: number;
        tn: number;
        fn: number;
      };
    };
  };
  results: Array<{
    id: string;
    testCaseId: string;
    query: string;
    passed: boolean;
    overallScore?: number;
    scoresDetail?: Record<string, number>;
    errorMessage?: string;
    durationMs?: number;
    // Ground truth comparison
    groundTruthPass?: boolean | null;
    predictedPass?: boolean;
    predictionCorrect?: boolean | null;
  }>;
}> {
  return fetchApi(`/evals/${encodeURIComponent(runId)}`);
}

// Trigger a new eval run
export async function triggerEvalRun(name?: string, testCaseIds?: string[]): Promise<{
  success: boolean;
  runId: string;
  status: string;
  message: string;
  progress?: {
    processed: number;
    total: number;
    passed: number;
    failed: number;
  };
}> {
  return fetchApi('/evals/run', {
    method: 'POST',
    body: JSON.stringify({ name, testCaseIds }),
  });
}

// Continue a running eval run (process next batch of test cases)
export async function continueEvalRun(runId: string): Promise<{
  success: boolean;
  status: string;
  progress?: {
    processed: number;
    total: number;
    passed: number;
    failed: number;
    batchIndex?: number;
    totalBatches?: number;
  };
  summary?: {
    total: number;
    passed: number;
    failed: number;
    avgScore: string;
  };
}> {
  return fetchApi(`/evals/${encodeURIComponent(runId)}/continue`, {
    method: 'POST',
  });
}

// List test cases
export async function listTestCases(options?: {
  limit?: number;
  category?: string;
  activeOnly?: boolean;
}): Promise<{
  testCases: EvalTestCase[];
  count: number;
}> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.category) params.set('category', options.category);
  if (options?.activeOnly !== undefined) params.set('active', String(options.activeOnly));
  return fetchApi(`/evals/test-cases?${params.toString()}`);
}

// Update a test case (including ground truth labels)
export async function updateTestCase(
  id: string,
  updates: {
    name?: string;
    query?: string;
    description?: string;
    expectedResponse?: string;
    expectedKeywords?: string[];
    excludedKeywords?: string[];
    expectedSources?: string[];
    category?: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    minScoreThreshold?: number;
    isActive?: boolean;
    datasetSplit?: 'train' | 'dev' | 'test';
    groundTruthPass?: boolean | null;
    groundTruthReasoning?: string | null;
  }
): Promise<{
  success: boolean;
  id: string;
  message: string;
}> {
  return fetchApi(`/evals/test-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Get execution trace (detailed phase inputs/outputs)
export interface ExecutionTrace {
  id: string;
  originalQuery: string;
  isThreadReply: boolean;
  threadContext?: unknown;
  status: string;
  totalDurationMs: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  estimatedCostUsd: number;
  createdAt: string;
  error?: { phase: string; message: string };
  phases: {
    queryExpander: {
      input: string;
      output: {
        enrichedQuery: string;
        searchTerms: string[];
        detectedAbbreviations: Array<{ abbrev: string; full: string }>;
      };
      model: string;
      durationMs: number;
      inputTokens: number;
      outputTokens: number;
    };
    retrieval: {
      input: { searchTerms: string[] };
      output: {
        totalResults: number;
        topScore: number;
        resultsPreview: Array<{ source: string; title: string; score: number; content?: string }>;
        allResults?: unknown[];
      };
      embeddingModel: string;
      embeddingDurationMs: number;
      searchDurationMs: number;
      durationMs: number;
    };
    assessment: {
      input: { query: string; resultsCount: number };
      output: {
        hasResults: boolean;
        isRelevantTopic: boolean;
        responseType: string;
      };
      model: string;
      durationMs: number;
      inputTokens: number;
      outputTokens: number;
    };
    reasoning: {
      input: { query: string; context: unknown };
      output: {
        thinking: string;
        answer: string;
      };
      model: string;
      durationMs: number;
      inputTokens: number;
      outputTokens: number;
    };
    formatting: {
      input: { rawAnswer: string };
      output: {
        formattedResponse: string;
        fallbackUsed: boolean;
      };
      model: string;
      durationMs: number;
      inputTokens: number;
      outputTokens: number;
    };
  };
}

export async function getExecutionTrace(executionId: string): Promise<{
  execution: ExecutionTrace;
}> {
  return fetchApi(`/evals/trace/${encodeURIComponent(executionId)}`);
}

// Submit human review for an eval result
export async function submitHumanReview(
  resultId: string,
  approved: boolean,
  notes?: string
): Promise<{
  success: boolean;
  resultId: string;
  approved: boolean;
  message: string;
}> {
  return fetchApi(`/evals/results/${encodeURIComponent(resultId)}/human-review`, {
    method: 'POST',
    body: JSON.stringify({ approved, notes }),
  });
}

// Create a new test case
export async function createTestCase(data: {
  name: string;
  query: string;
  description?: string;
  expectedResponse?: string;
  expectedKeywords?: string[];
  excludedKeywords?: string[];
  expectedSources?: string[];
  category?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  minScoreThreshold?: number;
}): Promise<{
  success: boolean;
  id: string;
  message: string;
}> {
  return fetchApi('/evals/test-cases', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Delete a test case
export async function deleteTestCase(id: string): Promise<{
  success: boolean;
  id: string;
  message: string;
}> {
  return fetchApi(`/evals/test-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ============================================================================
// Few-Shot Learning API
// ============================================================================

export interface FewShotStatus {
  status: 'collecting' | 'ready';
  totalReviews: number;
  approvedCount: number;
  rejectedCount: number;
  minimumRequired: number;
  categoryBreakdown: Record<string, { approved: number; rejected: number }>;
  message: string;
}

export async function getFewShotStatus(): Promise<FewShotStatus> {
  return fetchApi('/evals/few-shot-status');
}

// ============================================================================
// Scorer Prompt Config API
// ============================================================================

export type PromptScorerType = 'reasoner' | 'end_to_end';
export type ReasonerMetric = 'faithfulness' | 'hallucination' | 'completeness';
export type EndToEndMetric = 'answer_relevancy' | 'user_satisfaction' | 'keyword_coverage';
export type MetricName = ReasonerMetric | EndToEndMetric;

export interface ScorerPromptData {
  prompt: string;
  version: number;
  id: string;
}

export interface ScorerPromptsResponse {
  success: boolean;
  prompts: {
    reasoner: Record<ReasonerMetric, ScorerPromptData>;
    end_to_end: Record<EndToEndMetric, ScorerPromptData>;
  };
  metadata: {
    scorerTypes: PromptScorerType[];
    metrics: Record<PromptScorerType, MetricName[]>;
    displayNames: Record<MetricName, string>;
    descriptions: Record<MetricName, string>;
  };
  fromDefaults?: boolean;
}

export interface ScorerPromptHistoryItem {
  id: string;
  version: number;
  scorerType: PromptScorerType;
  metricName: MetricName;
  promptTemplate: string;
  isActive: boolean;
  createdAt: string;
  createdBy: {
    id: string;
    username: string | null;
    displayName: string | null;
  };
  notes: string | null;
}

export async function getScorerPrompts(): Promise<ScorerPromptsResponse> {
  return fetchApi('/evals/scorer-prompts');
}

export async function getScorerPromptHistory(
  scorerType: PromptScorerType,
  metricName: MetricName,
  limit: number = 20,
  offset: number = 0
): Promise<{
  success: boolean;
  history: ScorerPromptHistoryItem[];
}> {
  return fetchApi(`/evals/scorer-prompts/${scorerType}/${metricName}/history?limit=${limit}&offset=${offset}`);
}

export async function getScorerPromptById(promptId: string): Promise<{
  success: boolean;
  prompt: ScorerPromptHistoryItem;
}> {
  return fetchApi(`/evals/scorer-prompts/${encodeURIComponent(promptId)}`);
}

export async function saveScorerPrompt(data: {
  scorerType: PromptScorerType;
  metricName: MetricName;
  promptTemplate: string;
  notes?: string;
}): Promise<{
  success: boolean;
  message: string;
  prompt: {
    id: string;
    version: number;
    scorerType: PromptScorerType;
    metricName: MetricName;
    isActive: boolean;
    createdAt: string;
  };
}> {
  return fetchApi('/evals/scorer-prompts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function activateScorerPrompt(promptId: string): Promise<{
  success: boolean;
  message: string;
}> {
  return fetchApi(`/evals/scorer-prompts/${encodeURIComponent(promptId)}/activate`, {
    method: 'PUT',
  });
}

// ============================================================================
// Tickets
// ============================================================================

// Raw status from Periskope (stored in DB)
export type TicketStatus = 'open' | 'inprogress' | 'closed' | 'archived';

// Simplified display status for Oreo UI
export type DisplayStatus = 'open' | 'closed';

// Map Periskope status to display status
export function toDisplayStatus(status: TicketStatus): DisplayStatus {
  return status === 'closed' || status === 'archived' ? 'closed' : 'open';
}

// Map display status to Periskope status (for updates)
export function toPeriskopeStatus(displayStatus: DisplayStatus): TicketStatus {
  return displayStatus; // 'open' -> 'open', 'closed' -> 'closed'
}

// Map display status filter to Periskope statuses (for querying)
export function toPeriskopeStatusFilter(displayStatuses: DisplayStatus[]): TicketStatus[] {
  const result: TicketStatus[] = [];
  if (displayStatuses.includes('open')) {
    result.push('open', 'inprogress');
  }
  if (displayStatuses.includes('closed')) {
    result.push('closed', 'archived');
  }
  return result;
}

export type TicketPriority = 'needs_prioritisation' | 'standard' | 'urgent';
export type TicketCategory =
  | 'billing'
  | 'feature_request'
  | 'general_support'
  | 'hardware_issue'
  | 'logistics'
  | 'onboarding'
  | 'payment_issue'
  | 'tech_bug'
  | 'tech_question'
  | 'tech_request'
  | 'needs_categorisation';

export interface Ticket {
  id: string;
  periskope_ticket_id: string;
  periskope_chat_id: string | null;
  subject: string | null;
  status: TicketStatus;
  priority: TicketPriority | null;
  customer_phone: string | null;
  customer_name: string | null;
  assignee_email: string | null;
  assignee_name: string | null;
  assignee_admin_user_id: string | null;
  category: TicketCategory;
  is_urgent: number;
  labels: string | null;
  merchant_name: string | null;
  trigger_message_id: string | null;
  created_at: string;
  updated_at: string;
  first_response_at: string | null;
  resolved_at: string | null;
  last_message_at: string | null;
  periskope_synced_at: string | null;
  // AI Draft fields
  has_ai_draft: number;
  ai_category: TicketCategory | null;
  ai_category_confidence: number | null;
  rag_execution_id: string | null;
  // Slack bug report
  slack_bug_thread_url: string | null;
  // Extracted data fields
  merchant_id: string | null;
  extracted_order_ids: string | null; // JSON array
  extracted_outlet_name: string | null;
  extracted_outlet_id: string | null;
  extracted_issue_description: string | null;
  extracted_screenshots: string | null; // JSON array
  extracted_at: string | null;
  extraction_status: 'pending' | 'completed' | 'failed' | null;
  // KPI tracking fields
  is_one_response_resolution: number;
  one_response_marked_by: string | null;
  one_response_marked_at: string | null;
  // Original assignee tracking
  original_assignee_email: string | null;
  original_assignee_name: string | null;
  original_assignee_admin_user_id: string | null;
  originally_assigned_at: string | null;
}

export interface TicketMessage {
  id: string;
  ticket_id: string;
  periskope_message_id: string | null;
  direction: 'inbound' | 'outbound';
  message_type: 'text' | 'image' | 'document' | 'audio' | 'video' | 'note';
  content: string | null;
  media_url: string | null;
  sender_phone: string | null;
  sender_name: string | null;
  is_internal_note: number;
  created_at: string;
  periskope_timestamp: string | null;
}

export interface TicketActivity {
  id: string;
  ticket_id: string;
  action: string;
  old_value: string | null;
  new_value: string | null;
  actor_type: 'system' | 'webhook' | 'admin' | 'agent';
  actor_admin_user_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface TicketFilters {
  status?: TicketStatus[];
  assignee?: 'me' | 'unassigned' | 'all' | string;
  category?: TicketCategory[];
  priority?: TicketPriority[];
  isUrgent?: boolean;
  merchantName?: string;
  openForHours?: number;
  search?: string;
}

export interface TicketStats {
  total: number;
  byStatus: Record<string, number>;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  urgent: number;
  unassigned: number;
  openMoreThan2Days: number;
  // New mutually exclusive fields for filtered stats bar:
  openUrgent: number;         // All urgent open tickets (any age)
  openOldNonUrgent: number;   // Open >2 days AND not urgent
  openOther: number;          // Remaining open tickets (not urgent, not >2 days)
}

export async function getTickets(params?: {
  limit?: number;
  offset?: number;
  sortBy?: 'created_at' | 'last_message_at' | 'priority' | 'updated_at';
  sortOrder?: 'asc' | 'desc';
  filters?: TicketFilters;
}): Promise<{ tickets: Ticket[]; total: number }> {
  const query = new URLSearchParams();

  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.sortBy) query.set('sortBy', params.sortBy);
  if (params?.sortOrder) query.set('sortOrder', params.sortOrder);

  if (params?.filters) {
    const f = params.filters;
    if (f.status?.length) query.set('status', f.status.join(','));
    if (f.assignee) query.set('assignee', f.assignee);
    if (f.category?.length) query.set('category', f.category.join(','));
    if (f.priority?.length) query.set('priority', f.priority.join(','));
    if (f.isUrgent !== undefined) query.set('isUrgent', f.isUrgent.toString());
    if (f.merchantName) query.set('merchantName', f.merchantName);
    if (f.openForHours !== undefined) query.set('openForHours', f.openForHours.toString());
    if (f.search) query.set('search', f.search);
  }

  return fetchApi(`/admin/tickets?${query}`);
}

export async function getTicket(id: string): Promise<Ticket> {
  return fetchApi(`/admin/tickets/${id}`);
}

export async function getTicketMessages(id: string): Promise<{ messages: TicketMessage[] }> {
  return fetchApi(`/admin/tickets/${id}/messages`);
}

export async function getTicketActivity(id: string): Promise<{ activity: TicketActivity[] }> {
  return fetchApi(`/admin/tickets/${id}/activity`);
}

export async function updateTicket(
  id: string,
  updates: {
    status?: TicketStatus;
    priority?: TicketPriority;
    assigneeAdminUserId?: string | null;
    assigneeEmail?: string | null;
    category?: TicketCategory;
    isUrgent?: boolean;
    isOneResponseResolution?: boolean;
  }
): Promise<Ticket> {
  return fetchApi(`/admin/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function sendTicketMessage(
  id: string,
  data: { content: string; isNote?: boolean }
): Promise<TicketMessage> {
  return fetchApi(`/admin/tickets/${id}/message`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function resolveTicket(id: string): Promise<Ticket> {
  return fetchApi(`/admin/tickets/${id}/resolve`, {
    method: 'POST',
  });
}

export async function reopenTicket(id: string): Promise<Ticket> {
  return fetchApi(`/admin/tickets/${id}/reopen`, {
    method: 'POST',
  });
}

export async function getTicketStats(filters?: TicketFilters): Promise<TicketStats> {
  const params = new URLSearchParams();
  if (filters?.status?.length) params.set('status', filters.status.join(','));
  if (filters?.assignee && filters.assignee !== 'all') params.set('assignee', filters.assignee);
  if (filters?.category?.length) params.set('category', filters.category.join(','));
  if (filters?.priority?.length) params.set('priority', filters.priority.join(','));
  if (filters?.openForHours) params.set('openForHours', String(filters.openForHours));
  if (filters?.merchantName) params.set('merchantName', filters.merchantName);
  if (filters?.search) params.set('search', filters.search);

  const queryString = params.toString();
  return fetchApi(`/admin/tickets/stats${queryString ? `?${queryString}` : ''}`);
}

export async function syncTicketsFromPeriskope(): Promise<{ synced: number; errors: number; message: string }> {
  return fetchApi('/admin/tickets/sync', {
    method: 'POST',
  });
}

// ============================================================================
// Ticket AI Drafts
// ============================================================================

export interface TicketAIDraft {
  id: string;
  ticket_id: string;
  status: 'pending' | 'generating' | 'completed' | 'error';
  review_status: 'pending' | 'approved' | 'rejected';

  // Content
  original_query: string;
  phase4_formatted_response: string | null; // AI draft
  final_response: string | null; // Edited version (if any)

  // Review tracking
  channel: string;
  requires_review: number;
  was_edited: number;
  reviewed_by: string | null;
  reviewed_at: string | null;
  action_type: 'message' | 'note' | null;

  // Category suggestion
  suggested_category: TicketCategory | null;
  category_confidence: number | null;

  // Telemetry
  total_duration_ms: number | null;
  created_at: string;
  error_message: string | null;
}

export async function getTicketDraft(ticketId: string): Promise<{ draft: TicketAIDraft | null }> {
  return fetchApi(`/admin/tickets/${ticketId}/draft`);
}

export async function regenerateTicketDraft(ticketId: string): Promise<{ success: boolean; executionId: string }> {
  return fetchApi(`/admin/tickets/${ticketId}/draft/regenerate`, {
    method: 'POST',
  });
}

// Ticket Trace - reuses same phase structure as ConversationTrace
export interface TicketTrace {
  hasTrace: boolean;
  ticketId: string;
  message?: string; // When hasTrace is false
  executionId?: string;
  status?: 'completed' | 'error' | 'timeout' | 'pending' | 'generating';
  createdAt?: string;

  summary?: {
    totalDurationMs?: number;
    totalInputTokens?: number;
    totalOutputTokens?: number;
    estimatedCostUsd?: number;
    workflowModelPreset?: string;
  };

  phase1?: {
    name: string;
    durationMs?: number;
    model?: string;
    inputTokens?: number;
    outputTokens?: number;
    data: TracePhase1Data;
  };

  phase2?: {
    name: string;
    durationMs?: number;
    embeddingDurationMs?: number;
    searchDurationMs?: number;
    embeddingModel?: string;
    embeddingTokens?: number;
    data: TracePhase2Data;
  };

  phase3?: {
    name: string;
    durationMs?: number;
    model?: string;
    inputTokens?: number;
    outputTokens?: number;
    data: TracePhase3Data;
  };

  phase4?: {
    name: string;
    durationMs?: number;
    reasonerModel?: string;
    formatterModel?: string;
    reasonerInputTokens?: number;
    reasonerOutputTokens?: number;
    formatterInputTokens?: number;
    formatterOutputTokens?: number;
    data: TracePhase4Data;
  };

  error?: {
    phase?: string;
    message?: string;
    stack?: string;
  } | null;

  ticketContext?: {
    merchantName?: string;
    subject?: string;
    category?: string;
    priority?: string;
  };
}

export async function getTicketTrace(ticketId: string): Promise<TicketTrace> {
  return fetchApi(`/admin/tickets/${ticketId}/trace`);
}

// Ticket AI Draft Conversation
export interface DraftConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
}

export interface DraftConversation {
  conversationId: string;
  messages: DraftConversationMessage[];
}

export interface DraftChatResponse {
  response: string;
  messageId: string;
  executionId: string;
}

export async function getTicketDraftConversation(ticketId: string): Promise<{ conversation: DraftConversation | null }> {
  return fetchApi(`/admin/tickets/${ticketId}/draft/conversation`);
}

export async function sendTicketDraftChat(ticketId: string, message: string): Promise<DraftChatResponse> {
  return fetchApi(`/admin/tickets/${ticketId}/draft/chat`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

// ============================================================================
// Slack Integration
// ============================================================================

export interface SlackUser {
  id: string;
  name: string;
  realName: string;
  email?: string;
  avatar?: string;
}

export interface ReportTechBugRequest {
  title: string;
  description: string;
  products: string[];
  date: string;
  time: string;
  merchantOutlet: string;
  affectedOrders?: string;
  productOwners: string[];
  files?: File[];
}

export async function getSlackUsers(): Promise<{ users: SlackUser[] }> {
  return fetchApi('/admin/slack/users');
}

export async function reportTechBug(
  ticketId: string,
  data: ReportTechBugRequest
): Promise<{ success: boolean; threadUrl: string; message: string }> {
  // Use FormData if files are included
  if (data.files && data.files.length > 0) {
    const formData = new FormData();
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('products', JSON.stringify(data.products));
    formData.append('date', data.date);
    formData.append('time', data.time);
    formData.append('merchantOutlet', data.merchantOutlet);
    if (data.affectedOrders) {
      formData.append('affectedOrders', data.affectedOrders);
    }
    formData.append('productOwners', JSON.stringify(data.productOwners));

    // Append files
    for (const file of data.files) {
      formData.append('files', file);
    }

    const token = getAuthToken();
    const response = await fetch(`${API_BASE}/admin/tickets/${ticketId}/report-bug`, {
      method: 'POST',
      headers: {
        ...(token ? { 'X-Admin-Token': token } : {}),
      },
      body: formData,
    });

    if (response.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // No files - use JSON
  return fetchApi(`/admin/tickets/${ticketId}/report-bug`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================================================
// Dashboard Analytics (KPI Dashboard)
// ============================================================================

export interface TicketKPIs {
  totalClosed: number;
  totalOpen: number;
  oneResponseResolutions: number;
  closedWithoutAIChat: number;
  closedWithAIChat: number;
  aiSaidIDontKnow: number;
  avgResolutionTimeMinutes: number;
  openTicketBuckets: {
    lessThan1Hour: number;
    lessThan1Day: number;
    lessThan3Days: number;
    lessThan7Days: number;
    moreThan7Days: number;
  };
  closedByDay: Array<{ date: string; count: number }>;
  resolutionTimeByDay: Array<{ date: string; avgMinutes: number }>;
}

export interface AgentPerformance {
  agentId: string;
  agentName: string;
  agentEmail: string;
  ticketsClosed: number;
  pickedUpFromOthers: number;
  avgResolutionTimeMinutes: number;
  oneResponseResolutions: number;
  currentOpenTickets: number;
}

export interface RAGMetrics {
  totalConversations: number;
  avgConfidence: number;
  totalVectors: number;
  syncStatus: Record<string, { lastSync: string | null; count: number }>;
}

export interface DashboardAnalytics {
  period: { startDate: string; endDate: string };
  kpis: TicketKPIs;
  agentPerformance: AgentPerformance[];
  ragMetrics: RAGMetrics;
}

export type AnalyticsPeriod = '7d' | '30d' | '90d';

export async function getDashboardAnalytics(
  period: AnalyticsPeriod = '7d',
  category?: string
): Promise<DashboardAnalytics> {
  const params = new URLSearchParams({ period });
  if (category) {
    params.set('category', category);
  }
  return fetchApi(`/admin/analytics/dashboard?${params}`);
}

// ============================================================================
// Incentive Metrics
// ============================================================================

export interface IncentiveTier {
  name: string;
  target: string;
  reward: number;
  rewardUnit: 'flat' | 'per_ticket';
  attribution: string;
}

export interface AgentIncentive {
  agentEmail: string;
  agentName: string;
  volumeCount: number;
  volumePass: boolean;
  volumeReward: number;
  resolutionAvgMinutes: number;
  resolutionTicketCount: number;
  resolutionPass: boolean;
  resolutionReward: number;
  oneShotCount: number;
  oneShotReward: number;
  rescueCount: number;
  rescueReward: number;
  speedAvgMinutes: number | null;
  speedPass: boolean;
  speedReward: number;
  nomineeCount: number;
  nomineeReward: number;
  totalReward: number;
}

export interface IncentiveTicketDetail {
  id: string;
  subject: string | null;
  category: string;
  status: string;
  assignee_email: string | null;
  original_assignee_email: string | null;
  created_at: string;
  resolved_at: string | null;
  resolution_minutes: number | null;
  is_one_response_resolution: number;
  customer_name: string | null;
  merchant_name: string | null;
}

export interface IncentiveMetrics {
  period: { startDate: string; endDate: string };
  agents: AgentIncentive[];
  tiers: IncentiveTier[];
  teamTotals: {
    totalVolume: number;
    totalOneShot: number;
    totalRescue: number;
    totalReward: number;
  };
}

export interface ManualIncentiveEntry {
  id: string;
  period_start: string;
  period_end: string;
  agent_email: string | null;
  entry_type: 'speed' | 'nominee' | 'quality_collab';
  value: string | null;
  notes: string | null;
  entered_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function getIncentiveMetrics(
  startDate: string,
  endDate: string
): Promise<IncentiveMetrics> {
  const params = new URLSearchParams({ startDate, endDate });
  return fetchApi(`/admin/analytics/incentives?${params}`);
}

export async function getIncentiveDrillDown(
  agentEmail: string,
  metric: 'volume' | 'resolution' | 'oneshot' | 'rescue',
  startDate: string,
  endDate: string
): Promise<{ tickets: IncentiveTicketDetail[] }> {
  const params = new URLSearchParams({ agentEmail, metric, startDate, endDate });
  return fetchApi(`/admin/analytics/incentives/drilldown?${params}`);
}

export async function getManualIncentiveEntries(
  month: string
): Promise<{ entries: ManualIncentiveEntry[] }> {
  const params = new URLSearchParams({ month });
  return fetchApi(`/admin/analytics/incentives/manual?${params}`);
}

export async function saveManualIncentiveEntry(data: {
  periodStart: string;
  periodEnd: string;
  agentEmail: string | null;
  entryType: 'speed' | 'nominee' | 'quality_collab';
  value: string | null;
  notes: string | null;
}): Promise<{ success: boolean; entry: ManualIncentiveEntry }> {
  return fetchApi('/admin/analytics/incentives/manual', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

